import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquarePlus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const Stars = ({ n, onSet }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <button key={i} type="button" onClick={() => onSet && onSet(i + 1)} disabled={!onSet}>
        <Star className={`h-5 w-5 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export const FeedbackBlock = ({ vendorSlug }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", rating: 5, text: "", relationship: "", consent: false });
  const load = () => api.get(`/discovery/feedback?vendor_slug=${vendorSlug}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [vendorSlug]);

  const submit = async () => {
    if (!f.name || !f.text || !f.relationship) return toast.error("Name, relationship and feedback are required");
    if (!f.consent) return toast.error("Please confirm consent");
    try {
      const r = await api.post("/discovery/feedback", { vendor_slug: vendorSlug, ...f });
      toast.success(r.data.message); setOpen(false);
      setF({ name: "", email: "", rating: 5, text: "", relationship: "", consent: false });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };

  return (
    <div className="mt-6" data-testid="feedback-block">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-slate-900">Verified Customer Feedback</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="leave-feedback-btn"><MessageSquarePlus className="h-4 w-4 mr-1" /> Leave feedback</Button></DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Share your feedback</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Only feedback verified by our team is published as "Verified Customer Feedback".</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Your rating</Label><Stars n={f.rating} onSet={(v) => setF({ ...f, rating: v })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fb-name" /></div>
                <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="fb-email" /></div>
              </div>
              <div><Label>Your relationship *</Label><Input value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} placeholder="e.g. I used this service in Jan 2026" data-testid="fb-relationship" /></div>
              <div><Label>Feedback *</Label><Textarea rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="fb-text" /></div>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: v })} data-testid="fb-consent" className="mt-0.5" />
                I confirm I genuinely used this service and consent to my feedback being reviewed and published.
              </label>
            </div>
            <DialogFooter><Button className="w-full bg-teal-brand text-white" onClick={submit} data-testid="fb-submit">Submit for review</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((fb) => (
          <div key={fb.public_id} className="rounded-xl border border-emerald-200 bg-white p-3" data-testid={`feedback-${fb.public_id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">{fb.name}</span>
              <span className="rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase">Verified Customer Feedback</span>
            </div>
            <Stars n={fb.rating} />
            <p className="text-sm text-slate-700 mt-1 italic">“{fb.text}”</p>
            {fb.vendor_response && <p className="mt-2 text-xs text-slate-500 border-l-2 border-slate-200 pl-2"><b>Vendor response:</b> {fb.vendor_response}</p>}
          </div>
        ))}
        {!items.length && <p className="text-sm text-slate-400">No verified feedback yet. Be the first to share your experience.</p>}
      </div>
    </div>
  );
};
